import { useLocaleState, useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  doneCount,
  levelOf,
  missionViews,
  productMissionViews,
  totalXp,
} from "../domain/training/progress";
import { useTeamTraining } from "./useTraining";

/** Formation → Équipe (admins): each active user's level, missions done and last training activity. */
export const TeamTab = () => {
  const t = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const q = useTeamTraining(true);
  if (q.isPending) return <Skeleton className="h-40 w-full rounded-xl" />;
  if (q.isError)
    return (
      <div
        role="alert"
        className="flex flex-wrap items-center gap-2 text-sm text-destructive"
      >
        {t("gavion.training.load_error")}
        <Button size="sm" variant="outline" onClick={() => q.refetch()}>
          {t("gavion.training.retry")}
        </Button>
      </div>
    );
  const fmt = new Intl.DateTimeFormat(locale === "en" ? "en-CA" : "fr-CA", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "America/Toronto",
  });
  const rows = q.data.map((s) => {
    const views = missionViews(s);
    const product = productMissionViews(s);
    const xp = totalXp(views) + totalXp(product);
    return {
      productDone: doneCount(product),
      productTotal: product.length,
      productXp: totalXp(product),
      id: s.sales_id,
      name:
        [s.first_name, s.last_name].filter(Boolean).join(" ") ||
        `#${s.sales_id}`,
      xp,
      level: levelOf(xp),
      done: doneCount(views),
      total: views.length,
      last: s.last_activity,
    };
  });
  return (
    <section aria-labelledby="gavion-training-team" className="space-y-3">
      <h2 id="gavion-training-team" className="text-lg font-bold">
        {t("gavion.training.team.title")}
      </h2>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t("gavion.training.team.empty")}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground uppercase">
              <tr>
                <th scope="col" className="px-3 py-2">
                  {t("gavion.training.team.name")}
                </th>
                <th scope="col" className="px-3 py-2">
                  {t("gavion.training.team.level")}
                </th>
                <th scope="col" className="px-3 py-2">
                  {t("gavion.training.team.missions")}
                </th>
                <th scope="col" className="px-3 py-2">
                  {t("gavion.training.team.product")}
                </th>
                <th scope="col" className="px-3 py-2">
                  {t("gavion.training.team.xp")}
                </th>
                <th scope="col" className="px-3 py-2">
                  {t("gavion.training.team.last_activity")}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t">
                  <th scope="row" className="px-3 py-2 text-left font-semibold">
                    {r.name}
                  </th>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.level.level} ·{" "}
                    {t(`gavion.training.ranks.${r.level.rank}`)}
                  </td>
                  <td className="px-3 py-2 tabular-nums">
                    {r.done}/{r.total}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap tabular-nums">
                    {r.productDone}/{r.productTotal} · {r.productXp} XP
                  </td>
                  <td className="px-3 py-2 tabular-nums">{r.xp}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.last
                      ? fmt.format(new Date(r.last))
                      : t("gavion.training.team.never")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};
